import { CardTable, EntityName, Link, TableBottomCell } from '@/components';
import { TableColumn } from '@blockanalitica/ui/package/types/src/components/molecules/DataTable';
import {
  useTableProps,
  CryptoIcon,
  CurrencyValue,
  Flex,
  Box,
  TableSkeleton
} from '@blockanalitica/ui';
import { useApi, PaginatedResponse } from '@/api';
import { useNetwork } from '@/hooks';

type WalletResponse = {
  underlying_symbol: string;
  address: string;
  underlying_price: string | null;
  collateral_base: string | null;
  collateral_balance: string | null;
  market_symbol: string;
  market_address: string;
};

export default function CollateralsTable({
  marketAddress,
  walletAddress
}: {
  marketAddress: string;
  walletAddress: string;
}) {
  const network = useNetwork();
  const { sorting } = useTableProps({
    initialSorting: '-collateral_balance'
  });

  const [data, error] = useApi<PaginatedResponse<WalletResponse[]>>({
    url: `/markets/${marketAddress}/wallets/${walletAddress}/collaterals/`,
    queryParams: {
      sort: sorting.currentSorting
    }
  });

  if (error) {
    throw error;
  }

  if (!data) {
    return <TableSkeleton rows={3} columns={3} />;
  }

  const columns: TableColumn<WalletResponse>[] = [
    {
      header: 'Collateral Assets',
      getCell: (row) => (
        <EntityName
          entitySize="5"
          symbol1={
            <CryptoIcon
              name={row.underlying_symbol}
              address={row.address}
              network={network}
              size="5"
            />
          }>
          <Flex flexDirection="column" gap="3xs">
            <Box>{row.underlying_symbol}</Box>
          </Flex>
        </EntityName>
      )
    },
    {
      header: 'Asset Price',
      getCell: (row) => (
        <CurrencyValue
          value={row.underlying_price}
          options={{
            currency: {
              name: row.market_symbol,
              address: row.market_address,
              network: network
            }
          }}
        />
      ),
      sortingField: 'underlying_price',
      cellAlign: 'flex-end',
      headerAlign: 'flex-end'
    },
    {
      header: 'Collateral Supply',
      getCell: (row) => (
        <CurrencyValue
          value={row.collateral_balance}
          options={{
            currency: {
              name: row.underlying_symbol,
              address: row.address,
              network: network
            }
          }}
        />
      ),
      getCellBottom: (row) => (
        <TableBottomCell>
          <CurrencyValue
            value={row.collateral_base}
            options={{
              currencySize: '-1',
              currency: {
                name: row.market_symbol,
                address: row.market_address,
                network: network
              }
            }}
          />
        </TableBottomCell>
      ),
      sortingField: 'collateral_balance',
      cellAlign: 'flex-end',
      headerAlign: 'flex-end'
    }
  ];

  return (
    <CardTable<WalletResponse>
      title="Collateral"
      rows={data['results']}
      columns={columns}
      getKey={(row) => row.address}
      rowLinkProps={{
        to: (row) => `../${network}/${marketAddress}/${row.address}`,
        // @ts-ignore
        linkComponent: Link
      }}
      sorting={sorting}
    />
  );
}
